import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import FontKeys from '../FontKeys';
import gameData from '../gameData';
import Forces from '../math/Forces';
import SoundAssetKeys from '../SoundAssetKeys';
import BalloonContainer from './BalloonContainer';
import BalloonString from './BalloonString';

export default class BonusBalloon {
  /**
   * 
   * @param {Phaser.Scene} scene 
   * @param {BalloonContainer} container 
   */
  constructor(scene, container,startx, starty) {
    this.scene = scene;
    this.container = container;

    this.stringX = 0;
    this.stringY = 0;

    this.lblNumber = null;

    // Pop stuff
    this.curAnimation = 0;
    this.animationCount = 2;
    this.topPrefix = 'Balloon_Top0';
    this.popType = null;
    this.deflatePrefix = 'Balloon_Top01_Deflate0';
    this.animationWaiter = null;
    this.animationTime = 0.04;

    // Forces
    this.mass = 1;
    this.charge = 0;
    this.gravity = 50;
    this.kSpring = 10;
    this.cDamping = 0.9;

    // Incremental move
    this.angle = null;
    this.speed = null;
    this.ease = 0.13;
    this.distanceToTarget = 0;
    this.distanceTraveled = 0;
    this.direction = null;

    // p
    this.popScale = 1.4;
    this.popIncrement = 0.03;
    this.curScale = 1;
    this.isAlive = false;
    this.isActive = true;

    this.topLimit = 0;

    this.colorID = 3;

    this.isGood = false;
    this.num = null;

    this.spritePrefix = `Balloon_Top0${this.colorID}_Pop0`;
    this.balloonTop = ABCyaPhaser.Images.addImage(startx,starty, `Balloon_Top0${this.colorID}`, this.scene);

    this.txtNumber = ABCyaPhaser.Text.addText(startx,starty, gameData.fontSizes.med, 0xffffff, FontKeys._asapbold, '1', this.scene).setOrigin(0.5);

    this.balloonTopPop = ABCyaPhaser.Images.addImage(0,0,`Balloon_Top0${this.colorID}`, this.scene).setVisible(false);
    this.workingWidth = this.balloonTop.width;
    this.workingHeight = this.balloonTop.height;

    this.balloonString = new BalloonString(this.scene, startx, starty);
    this.balloonString.baseX = -2;
    this.balloonString.baseY = this.balloonTop.height / 2;
    this.balloonString.gravity = 9;

    if (this.container === null) {
      this.scene.add.existing(this.balloonString);
      this.scene.children.bringToTop(this.balloonTop);
      this.scene.children.bringToTop(this.balloonTopPop);
      this.scene.children.bringToTop(this.txtNumber);
    }
    else {
      this.container.add([this.balloonString, this.balloonTop, this.balloonTopPop, this.txtNumber]);
    }

    // set up physics
    this.pos = new Phaser.Math.Vector2(startx, starty);
    this.vel = new Phaser.Math.Vector2(0, 0);
    this.acc = new Phaser.Math.Vector2();
    this.attractor = new Phaser.Math.Vector2();
    this.omega = Math.sqrt(this.kSpring / this.mass);
    this.A = new Phaser.Math.Vector2(this.pos.x, this.pos.y);
    this.A.subtract({ x: this.attractor.x, y: this.attractor.y });
    this.B = new Phaser.Math.Vector2(this.vel.x, this.vel.y);
    this.B.multiply({ x: 1 / this.omega, y: 1 / this.omega });
    this.displ = new Phaser.Math.Vector2();
    this.springForce = new Phaser.Math.Vector2();
    this.dampForce = new Phaser.Math.Vector2();
    this.bobForce = new Phaser.Math.Vector2();
    this.totalForce = new Phaser.Math.Vector2();

    this.timeElapsed = 0;

    this.startPos = new Phaser.Math.Vector2();
    this.destPos = new Phaser.Math.Vector2();
    this.bottomLimit = this.scene.scale.height + 200;
  }

  reset (x,y, colorID = 1, num, isCorrect) {
    this.isGood = isCorrect;
    this.num = num;
    this.colorID = colorID;
    this.txtNumber.text = num.toString();
    this.topPrefix = `Balloon_Top0${this.colorID}`;
    this.spritePrefix = `Balloon_Top0${this.colorID}_Pop0`;
    this.deflatePrefix = `Balloon_Top0${this.colorID}_Deflate0`;
    this.balloonTop.setFrame(`Balloon_Top0${this.colorID}`);
    this.balloonString.baseX = -2;
    this.balloonString.baseY = this.balloonTop.height / 2;
    this.balloonTopPop.setFrame(`Balloon_Top0${this.colorID}`);
    this.balloonTopPop.scale = 1;
    this.isAlive = true;
    this.isActive = true;
    this.balloonString.resetRopePosition(x,y);
    this.txtNumber.setVisible(true);
    this.balloonTop.setVisible(true);
    this.balloonTopPop.setVisible(false);
    this.curAnimation = 0;
  }

  initHoverFloat (x,y) {
    this.state = STATE_HOVER;
    this.attractor.x = x;
    this.attractor.y = y;
  }

  initMoveTo (destX, destY, speed = 12) {
    this.state = STATE_IM;
    this.startPos.x = this.balloonTop.x;
    this.pos.x = this.balloonTop.x;
    this.startPos.y = this.balloonTop.y;
    this.pos.y = this.balloonTop.y;
    this.destPos.x = destX;
    this.destPos.y = destY;
    this.speed = speed * 60;// multiply by 60 to get the speed per second instead of speed per frame
    this.ease = 0.8;
    this.angle = Math.atan2(destY - this.pos.y, destX - this.pos.x);
    this.distanceToTarget = Phaser.Math.Distance.BetweenPoints(this.pos, this.destPos);
    this.distanceTraveled = 0;
  }

  tick (deltaSeconds) {
    const d = deltaSeconds;
    this.timeElapsed += d;
    this.pos.add({ x: this.vel.x * d, y: this.vel.y * d });
    this.moveUpdate();

    // Check collisions
    switch (this.state) {
      case STATE_MOVE_TO:
        this.floatTick(d);
        break;

      case STATE_HOVER:
        this.floatTick(d);
        break;

      case STATE_IM:
        this.incrementalMove(d);
        break;

      case STATE_POPPING:
        this.popTick(d);
        break;

      case BonusBalloon.STATE_DEFLATING:
        this.deflateTick(d);
        break;

      case STATE_FALLING:
        this.fallTick(d);
        break;
    }

    this.balloonString.tick(deltaSeconds, this.pos.x + this.balloonString.baseX, this.pos.y + this.balloonString.baseY);
  }

  moveUpdate () {
    this.balloonTop.x = this.pos.x;
    this.balloonTop.y = this.pos.y;
    this.txtNumber.x = this.pos.x;
    this.txtNumber.y = this.pos.y;
  }

  floatTick (dt) {
    this.pos.add({ x: this.vel.x * dt, y: this.vel.y * dt });
    this.calcForce();
    this.applyAccel();
    this.applyVelocity(dt);
  }

  deflateTick (dt) {
    if (this.animationWaiter <= 0) {
      this.curAnimation++;
      if (this.curAnimation < this.animationCount) {
        this.balloonTop.setFrame(this.deflatePrefix + this.curAnimation);
        this.animationWaiter = this.animationTime;
      }
    }
    else {
      this.animationWaiter -= dt;
    }

    if (this.pos.y > this.bottomLimit) {
      this.isActive = false;
      this.state = null;
    }
  }

  calcForce () {
    this.displ.set(this.pos.x - this.attractor.x, this.pos.y - this.attractor.y);
    this.displ.x *= 10;
    this.displ.y *= 0.8;

    Forces.spring(this.springForce, this.kSpring, this.displ);
    Forces.damping(this.cDamping, this.vel, this.dampForce);
    this.dampForce.y *= 2;
    this.dampForce.x *= 0.3;

    this.bobForce.reset();

    Forces.add([this.springForce, this.dampForce], this.totalForce);
  }

  applyAccel () {
    this.acc.set(this.totalForce.x, this.totalForce.y);
    this.acc.multiply({ x: 1 / this.mass,y: 1 / this.mass });
  }

  applyVelocity (d) {
    this.vel.add({ x: this.acc.x * d, y: this.acc.y * d });
  }

  incrementalMove (dt) {
    const workingX = this.pos.x + Math.cos(this.angle) * this.speed * this.ease * dt;
    const workingY = this.pos.y + Math.sin(this.angle) * this.speed * this.ease * dt;
    this.distanceTraveled = Phaser.Math.Distance.BetweenPoints(this.startPos, { x: workingX,y: workingY });
    if (this.distanceTraveled >= this.distanceToTarget) {
      this.balloonTop.emit('BALLOON_FINISHED', { balloon: this });
      this.pos.x = this.destPos.x;
      this.pos.y = this.destPos.y;
    }
    else {
      this.pos.x = workingX;
      this.pos.y = workingY;
    }
  }

  popTick (dt) {
    if (this.curScale < this.popScale) {
      this.curScale += this.popIncrement;
      this.balloonTopPop.scale = this.curScale;
    }
    else {
      this.balloonTopPop.scale = 1;
      this.vel.y = 520;
      this.vel.x = Phaser.Math.Between(10,20);
      this.curScale = 1;
      this.popType = Phaser.Math.Between(1,3) - 1;
      this.popPrefix = `${this.topPrefix}_Pop${this.popType}`;
      this.state = STATE_FALLING;
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._pop, this.scene);
    }
  }

  fallTick (dt) {
    if (this.animationWaiter <= 0) {
      this.curAnimation++;
      if (this.curAnimation > this.animationCount) {
        this.balloonTopPop.setVisible(false);
      }
      else {
        this.popType = Phaser.Math.Between(1,3) - 1;
        this.popPrefix = `${this.topPrefix}_Pop${this.popType}`;
        this.balloonTopPop.setFrame(this.spritePrefix + this.curAnimation);
        this.animationWaiter = this.animationTime;
      }
    }
    else {
      this.animationWaiter -= dt;
    }
    if (this.pos.y > this.bottomLimit) {
      this.isActive = false;
      this.state = null;
    }
  }

  pop () {
    this.animationCount = 2;
    this.curAnimation = 0;
    this.spritePrefix = `Balloon_Top0${this.colorID}_Pop0`;
    this.state = STATE_POPPING;
    this.animationWaiter = this.animationTime;
    this.balloonTopPop.x = this.pos.x;
    this.balloonTopPop.y = this.pos.y;
    this.balloonTop.setVisible(false);
    this.txtNumber.setVisible(false);
    this.balloonTopPop.setVisible(true);
    this.isAlive = false;
  }

  initFloatTo (destX, destY) {
    this.attractor.x = destX;
    this.attractor.y = destY;
    this.kSpring = 0.8;
    this.cDamping = 3.6;
    this.state = STATE_MOVE_TO;
  }

  checkHit (x,y) {
    const pt = this.balloonTop.getLocalPoint(x,y);
    const alpha = this.scene.textures.getPixelAlpha(pt.x, pt.y, this.balloonTop.texture.key, this.balloonTop.frame.name);
    return alpha && alpha > 0;
  }

  deflate (vy = 420) {
    this.animationCount = 5;
    this.curAnimation = 0;
    this.spritePrefix = `Balloon_Top0${this.colorID}_Deflate0`;
    this.balloonString.baseX = 1;
    this.balloonString.baseY = 10;
    this.state = STATE_DEFLATING;
    this.animationWaiter = this.animationTime;
    this.balloonTopPop.x = this.pos.x;
    this.balloonTopPop.y = this.pos.y;
    this.txtNumber.setVisible(false);
    this.isAlive = false;
    this.vel.y = vy;
    this.vel.x = Phaser.Math.Between(12,24);
  }
}

const STATE_MOVE_TO = 10;
const STATE_IM = 11;
const STATE_HOVER = 20;
const STATE_POPPING = 40;
const STATE_DEFLATING = 41;
const STATE_FALLING = 50;